import type { CallFees } from '@relayprotocol/relay-sdk'
import type { Logger } from './types'

export interface FunRelayFeeItem {
  b: number
  v: number
  config: {
    options: Record<string, unknown>
  }
  headers: Record<string, string>
  /** Rolling-window subsidy spend cap for this client, if configured. */
  rollingCap?: {
    /** Rolling window length, in seconds. */
    capInterval: number
    /** Max subsidy spend allowed within the window, in USD. */
    capAmount: number
  }
  /** Enforcement behavior when the rolling cap can't be checked. Omitted defers to FAIL_CLOSE. */
  failurePolicy?: 'FAIL_OPEN' | 'FAIL_CLOSE'
}

export async function getFunRelayFees({
  clientId,
  estUsdValue,
  fromTokenAddress,
  fromChainId,
  recipientAddress,
  senderAddress,
  toTokenAddress,
  toChainId,
  usePermit,
}: {
  clientId: string
  estUsdValue: number
  fromTokenAddress: string
  fromChainId: string
  recipientAddress: string | undefined
  senderAddress: string | undefined
  toTokenAddress: string
  toChainId: string
  /**
   * Optional explicit user preference for the source-side authorization
   * step. Forwarded to frog as a query param so its `getUsePermit` can
   * honor the FE toggle's choice. When omitted, frog falls back to its
   * legacy auto-gasless decision based on (client, chain, token, gas).
   */
  usePermit?: boolean
}): Promise<FunRelayFeeItem> {
  const params = new URLSearchParams({
    clientId,
    estUsdValue: estUsdValue.toString(),
    fromTokenAddress,
    fromChainId,
    toTokenAddress,
    toChainId,
    ...(recipientAddress ? { recipientAddress } : {}),
    ...(senderAddress ? { senderAddress } : {}),
    ...(usePermit !== undefined ? { usePermit: String(usePermit) } : {}),
  })
  const url = `https://frog.fun.xyz/api/fee?${params.toString()}`
  const response = await fetch(url)
  if (!response.ok) {
    throw Error(response.statusText)
  }

  const data = await response.json()
  return data
}

export function parseRelayFees({
  fees,
  fromChainIdRelay,
  fromTokenAddressRelay,
  logger,
  sourceAssetDecimals,
  sourceUnitPrice,
}: {
  fees: CallFees | undefined
  fromChainIdRelay: number
  fromTokenAddressRelay: string
  logger: Logger
  sourceAssetDecimals: number
  sourceUnitPrice: number
}) {
  // Gas fees, denominated in source chain native token
  const relayGasFeesUsd = Number.parseFloat(fees?.gas?.amountUsd || '0')

  // App fees, *usually* denominated in source chain token
  const areFunLpFeesInSourceChainToken =
    fees?.app?.currency?.chainId === fromChainIdRelay &&
    fees?.app?.currency?.address?.toLowerCase() ===
      fromTokenAddressRelay.toLowerCase()
  const funLpFeesUsd = Number.parseFloat(fees?.app?.amountUsd || '0')
  const funLpFeesFromAmount = areFunLpFeesInSourceChainToken
    ? Number.parseFloat(fees?.app?.amountFormatted || '0')
    : funLpFeesUsd / sourceUnitPrice || 0
  const funLpFeesFromAmountBaseUnit = areFunLpFeesInSourceChainToken
    ? BigInt(fees?.app?.amount || '0')
    : BigInt(Math.floor(funLpFeesFromAmount * 10 ** sourceAssetDecimals))

  // Relay fees, *usually* denominated in source chain token
  const areRelayLpFeesInSourceChainToken =
    fees?.relayer?.currency?.chainId === fromChainIdRelay &&
    fees?.relayer?.currency?.address?.toLowerCase() ===
      fromTokenAddressRelay.toLowerCase()
  const relayLpFeesUsd = Number.parseFloat(fees?.relayer?.amountUsd || '0')
  const relayLpFeesFromAmount = areRelayLpFeesInSourceChainToken
    ? Number.parseFloat(fees?.relayer?.amountFormatted || '0')
    : relayLpFeesUsd / sourceUnitPrice || 0
  const relayLpFeesFromAmountBaseUnit = areRelayLpFeesInSourceChainToken
    ? BigInt(fees?.relayer?.amount || '0')
    : BigInt(Math.floor(relayLpFeesFromAmount * 10 ** sourceAssetDecimals))

  const totalLpFeesUsd = funLpFeesUsd + relayLpFeesUsd
  const totalLpFeesFromAmount = funLpFeesFromAmount + relayLpFeesFromAmount
  const totalLpFeesFromAmountBaseUnit =
    funLpFeesFromAmountBaseUnit + relayLpFeesFromAmountBaseUnit

  logger.info('parseRelayFees', {
    relayGasFeesUsd,
    funLpFeesUsd,
    funLpFeesFromAmount,
    funLpFeesFromAmountBaseUnit,
    relayLpFeesUsd,
    relayLpFeesFromAmount,
    relayLpFeesFromAmountBaseUnit,
    totalLpFeesUsd,
    totalLpFeesFromAmount,
    totalLpFeesFromAmountBaseUnit,
  })

  return {
    relayGasFeesUsd,
    totalLpFeesUsd,
    totalFeesUsd: relayGasFeesUsd + totalLpFeesUsd,
    totalFeesFromAmount: totalLpFeesFromAmount,
    totalFeesFromAmountBaseUnit: totalLpFeesFromAmountBaseUnit,
  }
}
